import axios from "axios";
import { getSpotifyAccessToken, isSpotifyAccessTokenValid } from "./spotify-auth";
import { setNewLocation, localStorageGet, localStorageSet } from "./window";

const spotifyAxios = axios.create({
  baseURL: 'https://api.spotify.com/v1',
  timeout: 1000
});

const setAccessToken = () => {
  if (!isSpotifyAccessTokenValid()) {
    setNewLocation('/spotifyauth');
    return false;
  }
  // Check if access token is valid first and redirect if not
  const access_token = getSpotifyAccessToken();

  spotifyAxios.defaults.headers.common['Authorization'] = "Bearer " + access_token;
  return true;
};

const getMe = () => {
  if (!setAccessToken()) {
    return Promise.reject({error: 'Invalid token'});
  }

  const access_token = getSpotifyAccessToken();
  const profileFromLocalStorage = getSpotifyProfile(access_token);

  if (profileFromLocalStorage) {
    return Promise.resolve(profileFromLocalStorage);
  }

  return spotifyAxios.get('/me').then(function (response) {
    const data = response.data;
    const has_image = data.images && data.images.length > 0;
    const image_url = has_image ? data.images[0].url : null;

    const profile = {
      display_name: data.display_name,
      spotify_open_url: data.external_urls ? data.external_urls.spotify : '',
      image_url,
      id: data.id,
    };

    setSpotifyProfile({[access_token]: profile});

    return profile;
  })
  .catch(function (error) {
    return {
      error: "Failed to load profile. Try again."
    }
  });
};

// Consider a caching strategy
const getMyPlaylists = () => {
  if (!setAccessToken()) {
    return Promise.reject({error: 'Invalid token'});
  }

  const access_token = getSpotifyAccessToken();

  return new Promise((resolve, reject) => {
    getMyPlaylistsNext([], '/me/playlists?limit=50', resolve, reject, 0);
  });
};

const getMyPlaylistsNext = (playlists, next, resolve, reject, retries) => {
  if (next) {
    spotifyAxios.get(next).then(
      (response) => {
        const { data: { items, next } } = response;

        const bareItems = items.map((item) => ({ playlistId: item.id, playlistName: item.name, songCount: item.tracks.total, ownerId: item.owner.id }));
        playlists = playlists.concat(bareItems); // can filter down what I need
        getMyPlaylistsNext(playlists, next, resolve, reject);
      },
      () => {
        if (retries < 2) {
          getMyPlaylistsNext(playlists, next, resolve, reject, retries + 1);
        } else {
          alert('Failed to load Spotify playlists after several retries - please refresh the page or report the issue to playlistbuddy.app@gmail.com');
          reject();
        }
      }
    );
  } else {
    resolve({ playlists });
  }
};

// GET https://api.spotify.com/v1/me/tracks USERS tracks

// Would be nice to trim down the artist object to only name
const getPlaylistTracks = ({ ownerId, playlistId }) => {
  if (!setAccessToken()) {
    return Promise.reject({error: 'Invalid token'});
  }

  const access_token = getSpotifyAccessToken();

  return new Promise((resolve, reject) => {
    getPlaylistTracksNext([], `users/${ownerId}/playlists/${playlistId}/tracks?limit=100&fields=next,items(track(name,album(name),artists,preview_url,popularity,duration_ms))`, resolve, reject, 0);
  });
};

const convertToMinutesSeconds = (ms) => {
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(0);
  return minutes + ":" + (seconds < 10 ? '0' : '') + seconds;
};

// Figure out how to cancel if long playlist is being loaded and use clicks away
const getPlaylistTracksNext = (tracks, next, resolve, reject, retries) => {
  if (next) {
    spotifyAxios.get(next).then(
      (response) => {
        const { data: { items, next } } = response;

        const bareItems = items.map((item) => {
          const track = item.track;
          const artistName = track.artists.map((artist) => artist.name).join(', ');

          return {
            songName: track.name,
            artistName,
            albumName: track.album.name,
            popularity: track.popularity,
            preview: track.preview_url,
            duration: convertToMinutesSeconds(track.duration_ms),
          };
        });

        tracks = tracks.concat(bareItems);
        getPlaylistTracksNext(tracks, next, resolve, reject);
      },
      () => {
        if (retries < 3) {
          getPlaylistTracksNext(tracks, next, resolve, reject, retries + 1);
        } else {
          alert('Failed to load Spotify playlist after several retries - please refresh the page or report the issue to playlistbuddy.app@gmail.com');
          reject();
        }
      }
    );
  } else {
    // Implement some sort of caching with snapshot_id
    resolve({ tracks });
  }
};

const getSpotifyProfile = (access_token) => {
  const profile = JSON.parse(localStorageGet('PB_SPOTIFY_PROFILE') || '{}');

  return profile[access_token];
};

const setSpotifyProfile = (profile) => {
  return localStorageSet('PB_SPOTIFY_PROFILE', JSON.stringify(profile));
};

export {
  getMe,
  getMyPlaylists,
  getPlaylistTracks
};



// WEBPACK FOOTER //
// ./src/lib/spotify-api.js